import { useEffect } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateRole } from '../hooks/use-roles'
import type { Role } from '../types'

const updateRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required')
    .max(50, 'Role name must not exceed 50 characters')
    .regex(
      /^[A-Z_]+$/,
      'Role name must be uppercase letters and underscores only'
    ),
  description: z
    .string()
    .max(255, 'Description must not exceed 255 characters')
    .optional(),
})

type UpdateRoleFormValues = z.infer<typeof updateRoleSchema>

interface EditRoleDialogProps {
  role: Role | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditRoleDialog({
  role,
  open,
  onOpenChange,
}: EditRoleDialogProps) {
  const { t } = useTranslation()
  const { mutate: updateRole, isPending } = useUpdateRole()

  const form = useForm<UpdateRoleFormValues>({
    resolver: zodResolver(updateRoleSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  })

  // Update form values when role changes
  useEffect(() => {
    if (role) {
      form.reset({
        name: role.name,
        description: role.description || '',
      })
    }
  }, [role, form])

  const onSubmit = (data: UpdateRoleFormValues) => {
    if (!role) return

    updateRole(
      {
        id: role.id,
        data: {
          name: data.name,
          description: data.description || undefined,
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
    }
    onOpenChange(newOpen)
  }

  if (!role) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[500px]' dir={t('dir') as 'ltr' | 'rtl'}>
        <DialogHeader>
          <DialogTitle>{t('roles.dialog.edit.title', 'Edit Role')}</DialogTitle>
          <DialogDescription>
            {t('roles.dialog.edit.description', 'Update role information. Role names must be uppercase.')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('roles.dialog.roleName', 'Role Name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('roles.dialog.roleNamePlaceholder', 'MANAGER')}
                      {...field}
                      disabled={isPending || !role.updatesAllowed}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('roles.dialog.description', 'Description')} ({t('common.optional', 'Optional')})</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('roles.dialog.descriptionPlaceholder', 'Manager role with specific permissions')}
                      rows={3}
                      {...field}
                      disabled={isPending || !role.updatesAllowed}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!role.updatesAllowed && (
              <p className='text-muted-foreground text-sm'>
                {t('roles.dialog.readonly', 'This role cannot be modified.')}
              </p>
            )}

            <DialogFooter className='gap-2 sm:gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
                className='flex-1 sm:flex-1'
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type='submit'
                disabled={isPending || !role.updatesAllowed}
                className='flex-1 sm:flex-1'
              >
                {isPending ? t('common.saving', 'Updating...') : t('roles.dialog.edit.save', 'Update Role')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
